import { useCallback } from 'react';
import { useQuery as useApolloQuery } from '@apollo/client/react';
import { useMutation } from '@tanstack/react-query';
import { GET_KNOWLEDGE_BASES } from '../../lib/graphql/queries/organizations';
import { createKnowledgeBase, updateKnowledgeBase } from '../../api/remiApi';

export interface KnowledgeBaseItem extends Record<string, unknown> {
  question: string;
  answer: string;
}

export interface KnowledgeBase {
  id: string;
  kb_name: string;
  knowledge_base: KnowledgeBaseItem[];
  organization_id: string;
  type: string;
}

interface GQLKnowledgeBase {
  kbId: string;
  kbName: string;
  kbType: string | null;
  knowledgeBase: unknown;
  organizationId: string;
  createdAt: string | null;
  updatedAt: string | null;
}

interface GetKnowledgeBasesData {
  knowledgeBases: {
    knowledgeBases: GQLKnowledgeBase[];
    total: number;
    hasMore: boolean;
  };
}

const parseKBItems = (raw: unknown): KnowledgeBaseItem[] => {
  if (!raw) return [];
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as KnowledgeBaseItem[];
    } catch {
      return [];
    }
  }
  if (Array.isArray(raw)) return raw as KnowledgeBaseItem[];
  return [];
};

const mapGQLKnowledgeBase = (gql: GQLKnowledgeBase): KnowledgeBase => ({
  id: gql.kbId,
  kb_name: gql.kbName,
  knowledge_base: parseKBItems(gql.knowledgeBase),
  organization_id: gql.organizationId,
  type: gql.kbType || 'general',
});

interface UseGeneralKnowledgeBaseOptions {
  kbType?: string;
  flowType?: string;
  search?: string;
  sortBy?: string;
  limit?: number;
}

export const useGeneralKnowledgeBase = (
  organizationId?: string,
  enabled = false,
  options: UseGeneralKnowledgeBaseOptions = {}
) => {
  const {
    kbType = 'general',
    flowType,
    search,
    sortBy,
    limit = 10,
  } = options;

  const { data, loading, refetch: apolloRefetch } = useApolloQuery<GetKnowledgeBasesData>(
    GET_KNOWLEDGE_BASES,
    {
      variables: {
        organizationId: organizationId || '',
        limit,
        offset: 0,
        kbType,
        flowType,
        search,
        sortBy,
        sortOrder: 'ASC',
      },
      skip: !enabled || !organizationId,
      fetchPolicy: 'network-only',
    }
  );

  const knowledgeBases: KnowledgeBase[] = (
    data?.knowledgeBases?.knowledgeBases ?? []
  ).map(mapGQLKnowledgeBase);

  const refetch = useCallback(async () => {
    await apolloRefetch();
  }, [apolloRefetch]);

  const addKnowledgeBase = useMutation({
    mutationFn: async (kb: Omit<KnowledgeBase, 'id'> & { id?: string }) => {
      if (!organizationId) throw new Error('Organization ID is required');

      const payload = {
        kb_name: kb.kb_name,
        kb_type: kb.type,
        knowledge_base: kb.knowledge_base,
      };

      if (kb.id) {
        return updateKnowledgeBase(organizationId, kb.id, payload);
      } else {
        return createKnowledgeBase(organizationId, payload);
      }
    },
    onSuccess: () => {
      apolloRefetch();
    },
  });

  return {
    data: knowledgeBases,
    isLoading: loading,
    error: null as string | null,
    refetch,
    addKnowledgeBase,
  };
};
